/**
 * Users Service
 * API calls for user management (admin only)
 */

import axios from 'axios';
import apiClient from '../lib/axios';
import type { Role } from '../types';

export interface User {
  id: number | string; // Can be numeric user_id OR string "member_XXX" for uninvited members
  user_id?: number | null; // Actual numeric user ID from database (null for uninvited members)
  member_id: number | null;
  email: string;
  role: Role;
  is_active: boolean;
  record_type: 'user' | 'uninvited_member';
  first_name?: string;
  last_name?: string;
  has_profile_picture?: boolean;
  invitation_token?: string | null;
  invitation_sent_at?: string | null;
  invitation_expires_at?: string | null;
  invitation_revoked_at?: string | null;
  invitation_role?: Role | null;
  email_verified_at?: string | null;
  deactivated_at?: string | null;
  created_at: string;
  updated_at: string;
}

// Pagination interfaces
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: PaginationMeta;
}

export interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  role?: Role;
  status?: 'active' | 'invited' | 'invited_expired' | 'uninvited_member' | 'deactivated';
}

export interface InvitationData {
  email: string;
  member_id: number;
  role: Role;
  email_message?: string;
}

export interface InvitationResult {
  success: boolean;
  email: string;
  member_id: number;
  error?: string;
}

export interface BulkInvitationResponse {
  success: boolean;
  results: InvitationResult[];
  summary: {
    total: number;
    success: number;
    failed: number;
  };
}

export interface InvitationVerificationResponse {
  valid: boolean;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  member_id?: number;
  error?: 'invalid' | 'expired' | 'already_used' | 'revoked';
  message?: string;
}

export interface AcceptInvitationResponse {
  success: boolean;
  message: string;
  token: string;
  user: {
    id: number;
    email: string;
    role: Role;
    first_name: string;
    last_name: string;
    member_id: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  tenant?: {
    id: number;
    name: string;
    subdomain: string;
  };
}

export const usersService = {
  /**
   * Get paginated users with filters (admin only)
   */
  getPaginated: async (params: UsersQueryParams = {}): Promise<PaginatedResponse<User>> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.sort) queryParams.set('sort', params.sort);
    if (params.role) queryParams.set('role', params.role);
    if (params.status) queryParams.set('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString ? `/users?${queryString}` : '/users';

    const { data } = await apiClient.get<PaginatedResponse<User>>(url);
    return data;
  },

  /**
   * Get all users (admin only)
   * @deprecated Use getPaginated instead
   */
  getAll: async (): Promise<User[]> => {
    // Fetch all users by using a high limit
    const { data } = await apiClient.get<PaginatedResponse<User>>('/users?limit=100&page=1');
    // Handle both old array response and new paginated response
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.data)) {
      return data.data;
    }
    console.error('API returned unexpected data format for users:', data);
    return [];
  },

  /**
   * Get user by ID
   */
  getById: async (id: number): Promise<User> => {
    const { data } = await apiClient.get<User>(`/user/${id}`);
    return data;
  },

  /**
   * Send invitations (single or bulk)
   */
  sendInvitations: async (invitations: InvitationData | InvitationData[]): Promise<BulkInvitationResponse> => {
    const payload = Array.isArray(invitations)
      ? { invitations }
      : { invitations: [invitations] };

    try {
      const { data } = await apiClient.post<BulkInvitationResponse>('/invite', payload);
      console.log('✅ Invitation API success:', data);
      console.log('🔍 About to return data:', data);
      return data;
    } catch (error) {
      console.error('❌ Invitation API error:', error);
      // Handle 207 (partial success), 400 (all failed, caller's fault) and
      // 502 (all failed, mail transport down) as valid responses — the body
      // carries per-invitation results in every case.
      if (axios.isAxiosError(error) && error.response?.data) {
        console.log('⚠️ Returning error response as data:', error.response.data);
        return error.response.data as BulkInvitationResponse;
      }
      throw error;
    }
  },

  /**
   * Cancel pending invitation
   */
  cancelInvitation: async (memberId: number): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post<{ success: boolean }>('/invitation/cancel', {
      member_id: memberId,
    });
    return data;
  },

  /**
   * Resend expired invitation
   */
  resendInvitation: async (memberId: number): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post<{ success: boolean }>('/invitation/resend', {
      member_id: memberId,
    });
    return data;
  },

  /**
   * Update user role (admin only)
   */
  updateRole: async (userId: number, role: Role): Promise<User> => {
    const { data } = await apiClient.put<User>(`/user/${userId}/role`, { role });
    return data;
  },

  /**
   * Deactivate user (admin only)
   */
  deactivate: async (userId: number): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post<{ success: boolean }>('/user_status?action=deactivate', {
      user_id: userId,
    });
    return data;
  },

  /**
   * Reactivate deactivated user (admin only)
   */
  reactivate: async (userId: number): Promise<{ success: boolean; member_restored: boolean }> => {
    const { data } = await apiClient.post<{ success: boolean; member_restored: boolean }>('/user_status?action=reactivate', {
      user_id: userId,
    });
    return data;
  },

  /**
   * Delete user (admin only)
   */
  delete: async (userId: number): Promise<void> => {
    await apiClient.delete(`/user/${userId}`);
  },

  /**
   * Verify invitation token validity (public endpoint)
   */
  verifyInvitation: async (token: string): Promise<InvitationVerificationResponse> => {
    const { data } = await apiClient.get<InvitationVerificationResponse>(
      `/invitation/verify?token=${encodeURIComponent(token)}`
    );
    return data;
  },

  /**
   * Accept invitation and create account (public endpoint)
   */
  acceptInvitation: async (token: string, password: string): Promise<AcceptInvitationResponse> => {
    const { data } = await apiClient.post<AcceptInvitationResponse>('/invitation/accept', {
      token,
      password,
    });
    return data;
  },

  /**
   * Request new invitation link for expired invitations (public endpoint)
   */
  requestNewInvitation: async (payload: { token?: string; email?: string; turnstile_token?: string }): Promise<{ success: boolean; message: string }> => {
    const { data } = await apiClient.post<{ success: boolean; message: string }>('/invitation/request-new', payload);
    return data;
  },
};
